import { Dispatch, FC, SetStateAction, useCallback, useMemo } from "react";
import {
  ColDef,
  GridApi,
  GridState,
  RowClickedEvent,
  SelectionChangedEvent,
} from "ag-grid-community";
import { useWillUnmount } from "rooks";
import { useFlag } from "@unleash/proxy-client-react";
import { SxProps } from "@mui/system";
import { Theme } from "@mui/material";
import { getSavingsOpportunitiesTableColumns } from "./savingsOpportunitiesTableColumns";
import { AutoGroupColumnDefInnerRenderer } from "./cell-renderers/AutoGroupColumnDefInnerRenderer";
import { AgDataGrid } from "../../common/ag-grid/AgDataGrid";
import { useSavingsOpportunitiesViewsHierarchySaveHook } from "../utils/hooks/useSavingsOpportunitiesViewsHierarchySave.hook";
import { getAGGridAllSelectedRows } from "../../common/ag-grid/utils/helpers/getSelectedRowsFromApi";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { savingsOpportunityCurrentViewConfigSelector } from "@/store/savings-opportunities/selectors/views-hierarchy/current-view/savingsOpportunityCurrentViewConfigSelector";
import { setSavingsOpportunitiesSelectedRowsIds } from "@/store/savings-opportunities/savingsOpportunitiesSlice";
import { SavingsOpportunity } from "@/services/cloudchipr.api";
import { taskAvailableStatusPropertiesSelector } from "@/store/task-management/selectors/properties/taskAvailableStatusPropertiesSelector";
import { setSavingsOpportunitiesResourceDetailsDrawerResourceDataThunk } from "@/store/savings-opportunities/thunks/setSavingsOpportunitiesResourceDetailsDrawerResourceDataThunk";
import { useAppAbility } from "@/services/permissions";
import {
  opportunitiesGridDefaultColumnDef,
  opportunitiesGridRowSelection,
  opportunitiesGridWrapperStyles,
} from "@/components/pages/common/opportunities/utils/constants/gridConstants";

interface SavingsOpportunitiesDataGridProps {
  viewId?: string;
  gridApi: GridApi | null;
  data?: SavingsOpportunity[];
  setGridApi: Dispatch<SetStateAction<GridApi | null>>;
  wrapperStyles?: SxProps<Theme>;
  hiddenColumns: Set<string>;
  saveGridState?: boolean;
}

export const SavingsOpportunitiesDataGrid: FC<
  SavingsOpportunitiesDataGridProps
> = ({
  data,
  viewId,
  gridApi,
  setGridApi,
  wrapperStyles,
  hiddenColumns,
  saveGridState,
}) => {
  const dispatch = useAppDispatch();
  const statuses = useAppSelector(taskAvailableStatusPropertiesSelector);

  const { can } = useAppAbility();
  const canEditOpportunity = can("edit", "recommendation") && saveGridState;

  const enableBillingCostAndForecastedEOMForOpportunities = useFlag(
    "EnableBillingCostAndForecastedEOMForOpportunities",
  );

  const config = useAppSelector(savingsOpportunityCurrentViewConfigSelector);

  const columnDefs = useMemo(
    () =>
      getSavingsOpportunitiesTableColumns(
        statuses,
        enableBillingCostAndForecastedEOMForOpportunities,
        hiddenColumns,
      ),
    [
      statuses,
      enableBillingCostAndForecastedEOMForOpportunities,
      hiddenColumns,
    ],
  );

  const { onStateDebouncedUpdate } =
    useSavingsOpportunitiesViewsHierarchySaveHook({
      gridApi,
    });

  const configs = useMemo(() => {
    return JSON.parse(config?.gridConfig ?? "{}") as GridState;
  }, [config]);

  const rowSelectHandler = useCallback(
    ({ api }: SelectionChangedEvent) => {
      const selectedRows = getAGGridAllSelectedRows(api);

      dispatch(
        setSavingsOpportunitiesSelectedRowsIds(
          selectedRows.map((row) => row.id),
        ),
      );
    },
    [dispatch],
  );

  const getChildCount = useCallback((data: any) => data.count, []);

  const rowClickHandler = useCallback(
    (e: RowClickedEvent<SavingsOpportunity>) => {
      const data = e.data;

      if (!data?.resource_id) {
        return;
      }

      gridApi?.deselectAll();

      dispatch(
        setSavingsOpportunitiesResourceDetailsDrawerResourceDataThunk({
          opportunity: data,
          enableBillingCostAndForecastedEOMForOpportunities,
        }),
      );
    },
    [gridApi, dispatch, enableBillingCostAndForecastedEOMForOpportunities],
  );

  useWillUnmount(() => {
    dispatch(setSavingsOpportunitiesSelectedRowsIds([]));
  });

  if (!data) {
    return null;
  }

  return (
    <AgDataGrid
      key={viewId}
      rowHeight={55}
      rowData={data}
      columnDefs={columnDefs}
      rowGroupPanelShow="never"
      gridApiSetter={setGridApi}
      defaultColDef={defaultColDef}
      getChildCount={getChildCount}
      onRowClicked={rowClickHandler}
      onSelectionChanged={rowSelectHandler}
      autoGroupColumnDef={autoGroupColumnDef}
      initialState={viewId ? configs : undefined}
      rowSelection={opportunitiesGridRowSelection}
      wrapperStyles={{
        ...opportunitiesGridWrapperStyles,
        ...wrapperStyles,
      }}
      onStateUpdated={canEditOpportunity ? onStateDebouncedUpdate : undefined}
      configToRememberStateInStorage={
        viewId ? undefined : getConfigToRememberStateInStorage(saveGridState)
      }
    />
  );
};

const defaultColDef: ColDef = {
  ...opportunitiesGridDefaultColumnDef,
  cellStyle: ({ node }) =>
    !node.group && !!node.data?.account && !!node.data?.resource_id
      ? { cursor: "pointer" }
      : undefined,
};

const getConfigToRememberStateInStorage = (saveGridState?: boolean) => {
  return {
    uniqueNameAsKey: `all-savings-opportunities-view-${saveGridState}`,
  };
};

const autoGroupColumnDef: ColDef = {
  minWidth: 250,
  pinned: "left",
  sortable: false,
  enableRowGroup: false,
  suppressMovable: true,
  headerName: "Grouped by",
  cellRendererParams: { innerRenderer: AutoGroupColumnDefInnerRenderer },
};
